import React, { useState, useEffect, useMemo } from "react";
import Context from "../../Context";
import { useToast, Spinner } from "../../components/ui";
import { Header } from "../../components/modules";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/base";
import { HiOutlineDocumentSearch } from "react-icons/hi";
import { BiLogIn } from "react-icons/bi";
import { FiSearch, FiRefreshCw, FiX } from "react-icons/fi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import constant, { statuses } from "../../constants";
import { RiStore2Line } from "react-icons/ri";

const STATUS_FILTERS = [
  { key: "", label: "ทั้งหมด" },
  { key: statuses.PAID, label: "รออนุมัติ (Paid)" },
  { key: statuses.APPROVE, label: "อนุมัติแล้ว (Approved)" },
  { key: statuses.CANCEL, label: "ยกเลิก (Cancelled)" },
];

export const ProductOrders = ({ isPoint = false }) => {
  const { callApi, configs } = Context();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roundDate, setRoundDate] = useState(configs.roundDate);
  const [pages, setPages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState("shortId");
  const [status, setStatus] = useState("");

  useEffect(() => {
    getOrders();
  }, [roundDate, status, window.location.pathname]);

  const getOrders = async () => {
    const queries = [
      ["isPoint", isPoint],
      ["roundDate", roundDate],
    ];

    if (status) {
      queries.push(["status", status]);
    }

    if (searchType && searchValue.trim()) {
      queries.push([searchType, searchValue.trim()]);
    }

    setLoading(true);
    const result = await callApi(
      "products-getAllTransaction",
      {
        queries,
        limit: 1000,
        orderBy: ["createdAt", "desc"],
      },
      { notShowLoading: true }
    );

    const items = Array.isArray(result) ? result : [];
    const chunkMap = {};
    let pageIdx = 1;
    const chunkSize = 50;

    for (let i = 0; i < items.length; i += chunkSize) {
      chunkMap[pageIdx] = items.slice(i, i + chunkSize);
      pageIdx++;
    }

    setPages(chunkMap);
    setOrders(items);
    setCurrentPage(1);
    setLoading(false);
  };

  const currentList = useMemo(() => {
    return pages[currentPage] || [];
  }, [pages, currentPage]);

  const totalPages = Object.keys(pages).length;

  return (
    <div className="h-fit w-full max-w-[1600px] mx-auto pb-16 min-w-0">
      <Header
        header="Products"
        subHeader={isPoint ? "คำขอแลกคะแนน" : "คำสั่งซื้อสินค้า"}
        icon={<RiStore2Line className="text-navy" />}
      />

      {/* Main Container Card */}
      <div className="mt-4 bg-white rounded-md shadow-md border border-gray-200 p-4 flex flex-col gap-4">
        {/* Quick Status Tabs */}
        <div className="flex items-center gap-2 border-b border-gray-200 pb-3 overflow-x-auto scrollbar-none">
          {STATUS_FILTERS.map((f) => {
            const isActive = status === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setStatus(f.key)}
                className={`px-3.5 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer border ${
                  isActive
                    ? "bg-navy text-white border-navy shadow-xs"
                    : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {/* Filter & Search Form */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            getOrders();
          }}
          className="flex flex-wrap items-end gap-3"
        >
          {/* Round Date */}
          <div className="w-44">
            <label className="text-xs font-bold text-gray-700 block mb-1">
              งวดสลาก
            </label>
            <select
              className="h-10 w-full bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-none focus:border-navy transition-all cursor-pointer px-3"
              onChange={(e) => setRoundDate(e.target.value)}
              value={roundDate || ""}
            >
              {configs.roundDates?.map((date, index) => (
                <option key={index} value={date}>
                  {date}
                </option>
              ))}
            </select>
          </div>

          {/* Search Field Type */}
          <div className="w-44">
            <label className="text-xs font-bold text-gray-700 block mb-1">
              ค้นหาตาม
            </label>
            <select
              className="h-10 w-full bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-none focus:border-navy transition-all cursor-pointer px-3"
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
            >
              <option value="shortId">รหัสคำสั่งซื้อ</option>
              <option value="receiver.phone">เบอร์โทรศัพท์</option>
              <option value="userId">รหัสผู้ใช้</option>
              <option value="receiver.name">ชื่อผู้รับ</option>
            </select>
          </div>

          {/* Search Query Input */}
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-bold text-gray-700 block mb-1">
              คำค้นหา
            </label>
            <div className="relative flex items-center">
              <input
                className="h-10 w-full bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-none focus:border-navy transition-all px-3 placeholder:text-gray-400"
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="พิมพ์คำค้นหา..."
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={loading}
              className="h-10 px-4 flex items-center justify-center gap-1.5 bg-navy hover:bg-navy/90 text-white text-sm font-bold rounded-md shadow-xs transition-all duration-150 disabled:opacity-50 cursor-pointer"
            >
              <FiSearch className="w-4 h-4" />
              <span>ค้นหา</span>
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={() => {
                setSearchValue("");
                setStatus("");
                getOrders();
              }}
              className="h-10 px-3.5 flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-md border border-gray-300 shadow-xs transition-all cursor-pointer disabled:opacity-50"
            >
              <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              <span>รีเซ็ต</span>
            </button>
          </div>
        </form>

        {/* Total & Pagination Header */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <div className="text-sm font-semibold text-gray-600">
            พบทั้งหมด <span className="font-bold text-gray-900">{orders.length}</span> รายการ
          </div>

          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    className={`${
                      currentPage > 1
                        ? "cursor-pointer"
                        : "cursor-not-allowed hover:bg-transparent opacity-40"
                    } font-semibold`}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  />
                </PaginationItem>

                {Object.keys(pages).map((x) => (
                  <PaginationItem key={x}>
                    <PaginationLink
                      className={`${
                        currentPage === Number(x)
                          ? "bg-navy text-white hover:bg-navy font-bold shadow-xs"
                          : "text-gray-700"
                      } rounded-md cursor-pointer`}
                      onClick={() => setCurrentPage(Number(x))}
                    >
                      {x}
                    </PaginationLink>
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    className={`${
                      currentPage < totalPages
                        ? "cursor-pointer"
                        : "cursor-not-allowed hover:bg-transparent opacity-40"
                    } font-semibold`}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>

        {/* Table View */}
        <div className="w-full overflow-x-auto rounded-md border border-gray-200">
          <Table>
            <TableHeader className="bg-gray-50 border-b border-gray-200">
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap w-14">
                  ลำดับ
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  เวลาสร้างออเดอร์
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  รหัสคำสั่งซื้อ
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  รหัสผู้ใช้
                </TableHead>
                <TableHead className="font-bold text-gray-700 py-3 whitespace-nowrap">
                  ผู้รับ
                </TableHead>
                <TableHead className="font-bold text-gray-700 py-3 whitespace-nowrap">
                  เบอร์โทรศัพท์
                </TableHead>
                <TableHead className="font-bold text-gray-700 py-3 whitespace-nowrap">
                  รายการสินค้า
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  {isPoint ? "แต้มที่ใช้" : "ยอดชำระ (บาท)"}
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  สลิป
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  สถานะ
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  จัดการ
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11}>
                    <div className="w-full h-72 flex flex-col justify-center items-center gap-3 text-gray-500">
                      <Spinner className="w-8 h-8" color="border-navy" />
                      <p className="text-sm font-semibold">กำลังโหลดข้อมูลคำสั่งซื้อสินค้า...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="h-72 font-semibold text-center text-gray-400">
                    ไม่พบข้อมูลคำสั่งซื้อ
                  </TableCell>
                </TableRow>
              ) : (
                currentList.map((order, index) => (
                  <OrderRow
                    order={order}
                    key={order.id}
                    index={(currentPage - 1) * 50 + index}
                    onRefresh={getOrders}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

const OrderRow = ({ order, index, onRefresh }) => {
  const { callApi } = Context();
  const { toast } = useToast();
  const [tokenLoading, setTokenLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const handleCancelOrder = async () => {
    if (
      !window.confirm(
        `คุณต้องการ "ยกเลิก" คำสั่งซื้อ #${order.shortId || order.id?.slice(-5)} ใช่หรือไม่?\n(ระบบจะทำการคืนสต็อกสินค้าและคืนแต้มสะสมกลับให้ลูกค้าโดยอัตโนมัติ)`
      )
    ) {
      return;
    }

    setCancelLoading(true);
    const { status } = await callApi(
      "products-editStatus",
      {
        id: order.id,
        status: statuses.CANCEL,
      },
      { notShowLoading: true }
    );
    setCancelLoading(false);

    if (status) {
      toast({
        className: "bg-[rgb(59,176,101)]",
        title: "ยกเลิกคำสั่งซื้อสำเร็จ",
      });
      if (onRefresh) onRefresh();
    } else {
      toast({
        className: "bg-[#c15b78]",
        title: "ยกเลิกคำสั่งซื้อไม่สำเร็จ",
      });
    }
  };

  const statusBadge = useMemo(() => {
    switch (order.status) {
      case statuses.APPROVE:
        return (
          <span className="px-2.5 py-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md">
            อนุมัติแล้ว
          </span>
        );
      case statuses.PAID:
        return (
          <span className="px-2.5 py-1 text-xs font-bold text-navy bg-navy/10 border border-navy/20 rounded-md">
            ชำระแล้ว (รอตรวจ)
          </span>
        );
      case statuses.PENDING:
        return (
          <span className="px-2.5 py-1 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-md">
            รอชำระเงิน
          </span>
        );
      case statuses.CANCEL:
        return (
          <span className="px-2.5 py-1 text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-md">
            ยกเลิก
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-1 text-xs font-bold text-gray-600 bg-gray-100 border border-gray-200 rounded-md">
            {order.status || "-"}
          </span>
        );
    }
  }, [order.status]);

  const productsList = useMemo(() => {
    if (Array.isArray(order.product)) {
      return order.product.map((p, i) => (
        <div key={i} className="text-xs text-gray-700 leading-relaxed font-semibold">
          • {p.name || p.title || "สินค้า"}
        </div>
      ));
    }
    return (
      <span className="text-xs text-gray-700 font-semibold">
        {order.product?.name || "-"}
      </span>
    );
  }, [order.product]);

  return (
    <TableRow className="hover:bg-gray-50/70 transition-colors h-16">
      <TableCell className="text-center font-semibold text-gray-500 text-sm">
        {index + 1}
      </TableCell>
      <TableCell className="whitespace-nowrap text-center text-xs font-semibold text-gray-700">
        {order.createdAt
          ? new Date(order.createdAt).toLocaleDateString("th-TH", {
              year: "2-digit",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }) + " น."
          : "-"}
      </TableCell>
      <TableCell className="text-center font-mono font-bold text-sm text-gray-900">
        #{order.shortId}
      </TableCell>
      <TableCell className="font-mono text-center text-xs font-semibold text-gray-600">
        {order.userId}
      </TableCell>
      <TableCell className="font-semibold text-sm text-gray-800">
        {order.receiver?.name || "-"}
      </TableCell>
      <TableCell className="font-semibold text-sm text-gray-600">
        {order.receiver?.phone || "-"}
      </TableCell>
      <TableCell className="max-w-xs">{productsList}</TableCell>
      <TableCell className="text-center font-bold text-sm">
        {order.isPoint ? (
          <span className="text-navy">
            {(order.payPoint || order.point || 0).toLocaleString()} pts
          </span>
        ) : (
          <span className="text-gray-900">
            ฿{(Number(order.totalPrice ?? order.price) || 0).toLocaleString()}
          </span>
        )}
      </TableCell>
      <TableCell className="text-center">
        {order.slip ? (
          <img
            onClick={() => window.open(order.slip, "_blank")}
            src={order.slip}
            className="mx-auto w-10 h-10 object-cover rounded-md border border-gray-200 cursor-pointer hover:scale-105 transition-transform"
            alt="Slip"
          />
        ) : (
          <span className="text-gray-300 text-xs">-</span>
        )}
      </TableCell>
      <TableCell className="text-center whitespace-nowrap">
        {statusBadge}
      </TableCell>
      <TableCell className="text-center">
        <div className="flex items-center justify-center gap-1.5">
          <button
            title="เข้าสู่ระบบในชื่อผู้ใช้"
            className="p-2 bg-navy/10 hover:bg-navy/20 text-navy rounded-md transition-all cursor-pointer disabled:opacity-50"
            disabled={tokenLoading}
            onClick={async () => {
              setTokenLoading(true);
              const result = await callApi("users-addAdminToken", {
                userId: order.userId,
              });
              if (result?.adminToken) {
                window.open(constant.getAdminOrderUrl(result.adminToken));
              }
              setTokenLoading(false);
            }}
          >
            {tokenLoading ? (
              <Spinner className="w-3.5 h-3.5" color="border-navy" />
            ) : (
              <BiLogIn className="w-4 h-4" />
            )}
          </button>
          <button
            title="ดูรายละเอียดคำสั่งซื้อ"
            className="p-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-md transition-all cursor-pointer inline-flex items-center justify-center"
            onClick={() => window.open(`/product/order/${order.id}`, "_blank")}
          >
            <HiOutlineDocumentSearch className="w-4 h-4" />
          </button>
          {order.status !== statuses.CANCEL && (
            <button
              title="ยกเลิกคำสั่งซื้อ"
              disabled={cancelLoading}
              className="p-2 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 rounded-md transition-all cursor-pointer inline-flex items-center justify-center disabled:opacity-50"
              onClick={handleCancelOrder}
            >
              {cancelLoading ? (
                <Spinner className="w-4 h-4" color="border-rose-600" />
              ) : (
                <FiX className="w-4 h-4" />
              )}
            </button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};
